/* eslint-disable react-hooks/exhaustive-deps */
import {FC, useCallback, useEffect, useMemo, useState} from "react";
import {useLocation, useParams} from "react-router-dom";
import {useRecentlyViewedOperationalEntities} from "../../hooks";
import {ErrorPage, PageTemplate, RightSlideoutProps} from "../../layout";
import {ActivityIndicator} from "../../activity-indicator";
import {OperationalEntityProvider} from "../context";
import "../EntityPage.scss";
import "./OperationalEntityDetail.scss";
import {BackendError} from "@common-core/runtime-js/backend";
import {useQuery} from "@apollo/client";
import {
  LookupOperationalEntity,
  LookupOperationalEntityData,
  LookupOperationalEntityVariables
} from "../backend";
import {Endpoints, Environment} from "../../runtime";
import {BackToButton, Pages} from "../../back-buttons";
import {useTranslation} from "react-i18next";
import {CommonOrgComponentTab} from "../pbc";
import {useEnvironment} from "@common-core/react-runtime/context";
import {useAnalytics} from "@common-core/react-analytics";
import {ManagedTabs} from "../../managed-tabs";
import {OperationalOverviewTab} from "./overview-tab";
import {OperationalHierarchyTab} from "./hierarchy-tab";
import {OperationalEntity} from "@common-core/coat-operational-hierarchy-appsync-model";
import {PermissionBasedEditButton} from "../../action-buttons";
import {EditDbaNameModal} from "./overview-tab/EditDbaNameModal";
import {OnCompleteCallback} from "../../utils";
import {EDIT_DBA_NAME} from "../../access";
import {
  CommonOrgTabComponent,
  tabLabelKeyFor,
  tabLabelKeyFromHash
} from "../../pbc";
import {useItemIsVisible} from "../../visibility";
import {useSidebarItems} from "../../navigation";
import {Box} from "@interstate/components/Box";
import {useFlags} from "launchdarkly-react-client-sdk";
import {CommentsColumn} from "./comments/CommentsColumn";
import {ShowMore} from "@interstate/components/ShowMore";
import {features} from "../../features";
import {
  ChatBubbleLeftRightIcon,
  ComputerDesktopIcon
} from "@interstate/components/Icons";
import {ContactSupportForm} from "./support";
import {Button} from "@interstate/components/Button";

interface RouteParams {
  id: string;
}

/**
 * The Page concerns itself with routing information and extracts
 * what it needs to prepare the other components
 * for display
 *
 * @constructor
 */
export const OperationalEntityPage: FC = () => {
  const {id} = useParams<RouteParams>();
  const sidebarItems = useSidebarItems();
  interface LocationState {
    from: string;
    query: string;
  }
  const environment = useEnvironment<Environment>();
  const {t} = useTranslation();
  const {productEvent} = useAnalytics();
  const {addBrowsedOperationalEntity} = useRecentlyViewedOperationalEntities();
  const location = useLocation<LocationState>();
  const flags = useFlags();
  const displaySupportForm = flags[features.dev.supportForm.showSupportForm];
  const manageCommentsFlag =
    flags[features.pdt.contextualComments.manageComments];
  const [editingDbaName, setDbaNameEditing] = useState<boolean>(false);
  const [showComments, setShowComments] = useState<boolean>(false);
  const [commentsArePresent, setCommentsArePresent] = useState<boolean>(false);
  const [showSupportForm, setShowSupportForm] = useState<boolean>(false);
  const finishDbaNameEditing: OnCompleteCallback = () => {
    setDbaNameEditing(false);
  };
  const backToPage =
    location.state?.from === "/search" ? Pages.Search : Pages.Home;
  const {loading, error, data} = useQuery<
    LookupOperationalEntityData,
    LookupOperationalEntityVariables
  >(LookupOperationalEntity, {
    variables: {id},
    context: {
      endpoint: Endpoints.APPSYNC
    }
  });
  // // TODO: Add test around this - test on hierarchy?
  const translate = useCallback(
    (tab: string) => {
      return tab === "hierarchy" || tab === "overview"
        ? t(`entity-detail.${tab}`, {lng: "en"})
        : t(tabLabelKeyFromHash(tab), {lng: "en"});
    },
    [t]
  );
  const entity = data?.entity || ({} as OperationalEntity);
  useEffect(() => {
    if (entity.id) {
      addBrowsedOperationalEntity(entity.id);
    }
  }, [entity?.id]);

  useEffect(() => {
    const handleCommentsLoaded = (e: Event) => {
      const customEvent = e as CustomEvent<{count?: number}>;
      setCommentsArePresent((customEvent.detail?.count ?? 0) > 0);
    };
    window.addEventListener("commentsLoaded", handleCommentsLoaded);
    return () =>
      window.removeEventListener("commentsLoaded", handleCommentsLoaded);
  }, []);

  const isVisible = useItemIsVisible<CommonOrgTabComponent>();
  const tabComponents = useMemo<CommonOrgTabComponent[]>(
    () => environment.tabComponents.filter(isVisible),
    [environment.tabComponents, isVisible]
  );

  if (loading) {
    return (
      <PageTemplate
        id={"operational-entity-page"}
        title={"Operational Entity Page"}
        sidebarItems={sidebarItems}>
        <ActivityIndicator />
      </PageTemplate>
    );
  }

  // Handle Apollo errors first (network errors, GraphQL errors, etc.)
  if (error) {
    return (
      <ErrorPage error={error} severity={"error"} sidebarItems={sidebarItems} />
    );
  }

  // Then check if entity data is missing
  if (!data?.entity) {
    return (
      <ErrorPage
        error={new BackendError("Entity not found", 404)}
        logo={false}
        sidebarItems={sidebarItems}
      />
    );
  }

  const hideSupportForm = () => {
    setShowSupportForm(false);
  };

  const supportSlideout: RightSlideoutProps = {
    show: showSupportForm,
    onHide: hideSupportForm,
    id: "contact-support-slideout",
    className: "contact-support-slideout",
    header: t("contact-support.header"),
    children: (
      <ContactSupportForm
        dbaName={data.entity.organizationInformation.dbaName}
        commonOrgId={data.entity.id}
        hideSupportForm={hideSupportForm}
      />
    )
  };

  return (
    <PageTemplate
      id={"operational-entity-page"}
      title={"Operational Entity Page"}
      breadcrumbs={<BackToButton page={backToPage} />}
      sidebarItems={sidebarItems}
      rightSideSlideout={supportSlideout}>
      <OperationalEntityProvider entity={data.entity}>
        <div
          className={"operational-entity-detail"}
          data-testid={"operational-entity-detail"}>
          {editingDbaName && (
            <EditDbaNameModal
              id={data.entity.id}
              onComplete={finishDbaNameEditing}
              currentDbaName={data.entity.organizationInformation.dbaName}
            />
          )}
          <header className={"entity-detail-header"}>
            <h2 className={"entity-business-name"}>
              {data.entity.organizationInformation?.dbaName}
            </h2>
            <PermissionBasedEditButton
              onClick={() => setDbaNameEditing(true)}
              qualifier={"dba-name"}
              tooltip={t("entity-detail.edit-dba-name")}
              inline={true}
              permissions={[EDIT_DBA_NAME]}
            />

            <Box className={"operational-entity-detail-action-buttons"}>
              {displaySupportForm && (
                <Button
                  startIcon={<ComputerDesktopIcon />}
                  onClick={() => setShowSupportForm(true)}
                  id={"support-form"}>
                  {t("contact-support.button")}
                </Button>
              )}
              {manageCommentsFlag && (
                <Button
                  startIcon={
                    <Box>
                      <ChatBubbleLeftRightIcon />
                      <Box
                        id={"comments-indicator"}
                        className={commentsArePresent ? "visible" : "hidden"}
                      />
                    </Box>
                  }
                  onClick={() => setShowComments(!showComments)}
                  id={"comments-toggle"}>
                  {showComments
                    ? t("entity-detail.hide-comments")
                    : t("entity-detail.show-comments")}
                </Button>
              )}
            </Box>
          </header>
          <Box className={"operational-body"}>
            <Box className={"managed-tabs"}>
              <ManagedTabs
                id={"entity-detail-tabs"}
                initial={"overview"}
                onTabActivated={event => {
                  productEvent({
                    name: `entity_detail_tab_clicked`,
                    properties: {
                      location: "Dealer Details Overview",
                      value: `${translate(event.tabId)}`,
                      result: "Tab Displayed"
                    }
                  });
                }}>
                <OperationalOverviewTab
                  tabId={"overview"}
                  label={t("entity-tabs.overview")}
                  unmountInactive={true}
                />
                <OperationalHierarchyTab
                  tabId={"hierarchy"}
                  label={t("entity-tabs.hierarchy")}
                  unmountInactive={true}
                />
                {tabComponents.map(component => (
                  <CommonOrgComponentTab
                    key={component.element}
                    tabId={component.hash || component.element}
                    label={t(tabLabelKeyFor(component))}
                    unmountInactive={true}
                    component={component}
                    commonOrgId={data.entity.id}
                  />
                ))}
              </ManagedTabs>
            </Box>
            {manageCommentsFlag && (
              <ShowMore
                expand={showComments}
                orientation={"vertical"}
                id={"show-more-comments"}
                sx={{
                  flexBasis: showComments ? "33%" : "0%",
                  visibility: showComments ? "visible" : "hidden"
                }}
                data-testid={"show-more-comments"}>
                <CommentsColumn commonOrgId={data.entity.id} />
              </ShowMore>
            )}
          </Box>
        </div>
      </OperationalEntityProvider>
    </PageTemplate>
  );
};
